import { CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { CompactToggle } from '@/components/ui/toggle-setting'
import { cn } from '@/lib/utils'
import PlanCardShell from '@/components/plan/PlanCardShell'
import PlanFieldDelta from '@/components/plan/PlanFieldDelta'

export default function EligibilityCard({ id, title, description, reference, compareTo, enabled, onEnabledChange, minAge, onMinAgeChange, minServiceMonths, onMinServiceChange, minHours, onMinHoursChange, planYear, disabled, variant }) {
  const showDelta = Boolean(compareTo?.enabled && enabled)
  const ltptRuleApplies = Number(planYear) >= 2025
  return (
    <PlanCardShell
      id={id}
      disabled={disabled}
      variant={variant}
      title={title}
      description={description}
      titleAction={() => onEnabledChange(!enabled)}
      titleActionLabel="Toggle eligibility requirements"
      titleActionDisabled={disabled}
      headerAction={<CompactToggle label="Apply minimum eligibility requirements" checked={enabled} onCheckedChange={onEnabledChange} disabled={disabled} />}
      collapsible={false}
    >
      <CardContent className="space-y-4">
        {reference}
        <div className={cn('grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-3', !enabled && 'opacity-60')}>
          <div className="space-y-1">
            <Label htmlFor={`${id}-age`}>Min age<PlanFieldDelta value={minAge} compareValue={compareTo?.minAge} show={showDelta} /></Label>
            <NumberInput id={`${id}-age`} validationLabel="Minimum age" value={minAge} onValueChange={onMinAgeChange} min={0} integer step="1" disabled={!enabled} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${id}-service`}>Service (mo)<PlanFieldDelta value={minServiceMonths} compareValue={compareTo?.minServiceMonths} show={showDelta} /></Label>
            <NumberInput id={`${id}-service`} validationLabel="Minimum service (months)" value={minServiceMonths} onValueChange={onMinServiceChange} min={0} integer step="1" disabled={!enabled} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${id}-hours`}>Min hours<PlanFieldDelta value={minHours} compareValue={compareTo?.minHours} show={showDelta} /></Label>
            <NumberInput id={`${id}-hours`} validationLabel="Minimum hours" value={minHours} onValueChange={onMinHoursChange} min={0} integer step="1" disabled={!enabled} />
          </div>
        </div>
        <div className={cn('space-y-2 rounded-md bg-muted/30 p-3', !enabled && 'opacity-60')}>
          <div className="text-sm font-medium text-foreground">
            {ltptRuleApplies ? 'LTPT deferrals are applied for 2025+ plan years' : 'LTPT two-year proxy is not applied before 2025'}
          </div>
          <p className="text-xs text-muted-foreground">
            {ltptRuleApplies
              ? 'SECURE 2.0 requires employees with 500+ hours in each of 2 consecutive years (age 21+) to be allowed to make employee deferrals even if they miss the requirements above. Plans may still exclude them from employer contributions and testing, which is how this models them.'
              : 'The older 3-consecutive-year rule for 2024 is not modeled here.'}
          </p>
          <p className="text-xs text-muted-foreground">
            Data assumption: the employee list holds one year of hours, so an employee counts as LTPT when they meet the age
            rule, are not marked full-time, and have 500+ hours with 2+ years of service – a stand-in for the
            statute&rsquo;s &ldquo;500+ hours in each of two consecutive years,&rdquo; which needs prior-year hours this app
            doesn&rsquo;t collect.
          </p>
        </div>
      </CardContent>
    </PlanCardShell>
  )
}
